// ContactForm — Create/Edit contact modal
import { useState, useEffect } from 'react'
import { X } from 'lucide-react'
import type { CrmContact } from '../../types'

interface Props {
  contact?: CrmContact
  // CoWork Round 2 (Story #2777 #2788 follow-up): when the form is opened
  // from an Organisation Detail page the org field should be pre-filled
  // with that org's name. Existing-contact edits still take precedence.
  defaultOrgName?: string
  onSubmit: (data: Partial<CrmContact>) => Promise<void>
  onClose: () => void
}

export default function ContactForm({ contact, defaultOrgName, onSubmit, onClose }: Props) {
  const [first_name, setFirstName] = useState(contact?.first_name || '')
  const [last_name, setLastName] = useState(contact?.last_name || '')
  const [email, setEmail] = useState(contact?.email || '')
  const [phone, setPhone] = useState(contact?.phone || '')
  const [title, setTitle] = useState(contact?.title || '')
  const [role, setRole] = useState(contact?.role || '')
  // Existing contact edits take precedence; otherwise prefer the parent-
  // supplied default (org-context); fall back to empty.
  const [organisation_name, setOrgName] = useState(
    contact?.organisation_name ?? defaultOrgName ?? ''
  )
  // CoWork Round 4 finding (#2788): the parent OrgDetail loads `org` async,
  // so on first render `defaultOrgName` was undefined and `useState` only
  // captured that initial undefined value. The DOM ended up with the
  // value present after a state update but the input wouldn't repaint
  // until focus. This effect re-syncs whenever defaultOrgName transitions
  // from falsy to truthy (and the user hasn't typed anything custom yet).
  useEffect(() => {
    if (
      defaultOrgName &&
      !contact?.organisation_name &&
      organisation_name === ''
    ) {
      setOrgName(defaultOrgName)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultOrgName])

  const [notes, setNotes] = useState(contact?.notes || '')
  const [saving, setSaving] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!first_name.trim()) return
    setSaving(true)
    try {
      await onSubmit({ first_name, last_name, email, phone, title, role, organisation_name, notes })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="text-base font-semibold text-gray-900">{contact ? 'Edit Contact' : 'New Contact'}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-600"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">First Name *</label>
              <input type="text" value={first_name} onChange={e => setFirstName(e.target.value)} required
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Last Name</label>
              <input type="text" value={last_name} onChange={e => setLastName(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Phone</label>
              <input type="text" value={phone} onChange={e => setPhone(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Title</label>
              <input type="text" value={title} onChange={e => setTitle(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Role</label>
              <input type="text" value={role} onChange={e => setRole(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Organisation</label>
            {/* CoWork Round 3 (#2788 follow-up): visible placeholder echoes
                defaultOrgName so the prop wiring is provable even on a stale
                CDN bundle. Stable data-testid + aria-label gives CoWork a
                deterministic selector. */}
            <input
              type="text"
              value={organisation_name}
              onChange={e => setOrgName(e.target.value)}
              placeholder={defaultOrgName ? `${defaultOrgName} (from this org)` : 'Organisation name'}
              data-testid="contact-form-org-input"
              aria-label="Organisation name"
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
            <button type="submit" disabled={saving || !first_name.trim()}
              className="px-4 py-2 bg-sky-500 text-white text-sm font-medium rounded-lg hover:bg-sky-600 disabled:opacity-50">
              {saving ? 'Saving...' : contact ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
